import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Typography,
  Stack,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Menu,
  MenuItem,
  Fade,
  Chip
} from '@mui/material';
import { NotificationManager } from 'react-notifications';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  formatCurrencyBRL,
  formatDurationFromSeconds
} from '../../../../@core/utils/utilsFunctions';
import { CustomGrid, CustomInfosForm } from '../../../../assets/style/globalStyle';
import configAxios from '../../../../services/configAxios';
import Iconify from '../../../../@core/theme/Iconify';
import RenderPage from '../../../layouts/RenderPage';
import BreadcrumbsNav from '../../../components/multiplesComponents/BreadCrumbs';
import PipelineFilters from './PipelineFilters';
import { getStatusMeta, buildPipelineQuery } from './pipelineStatuses';

function TimeColumnHeader({ label, color, icon }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
      <Iconify icon={icon} width={14} height={14} sx={{ color: color + '.main' }} />
      <Box component="span" sx={{ color: color + '.main', fontWeight: 600 }}>
        {label}
      </Box>
    </Stack>
  );
}

TimeColumnHeader.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired
};

function toFunnelRow(item, meta) {
  return {
    statusvenda: item.statusvenda || meta.status,
    quantidade: Number(item.quantidade || 0),
    representatividade_percentual: Number(item.representatividade_percentual || 0),
    valor_total: Number(item.valor_total || 0)
  };
}

function buildFunnelOrdered(list) {
  return (list || []).map((item) => toFunnelRow(item, getStatusMeta(item.statusvenda)));
}

const PipelineFunnel = (props) => {
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [funnelOrdered, setFunnelOrdered] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [efficiency, setEfficiency] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    getFunnelInfos();
  }, []);

  function getFunnelInfos(nextFilters = filters) {
    setLoading(true);
    const query = buildPipelineQuery(nextFilters);
    Promise.all([
      configAxios.get(`/pipeline/funnel${query}`),
      configAxios.get(`/pipeline/indicators${query}`),
      configAxios.get(
        `/pipeline/efficiency${buildPipelineQuery({
          ...nextFilters,
          status: 'Aguardando cliente'
        })}`
      )
    ])
      .then(([funnelResp, indicatorsResp, efficiencyResp]) => {
        setFunnelOrdered(buildFunnelOrdered(funnelResp.data || []));
        setIndicators(indicatorsResp.data || []);
        setEfficiency(efficiencyResp.data || []);
      })
      .catch(() => {
        NotificationManager.warning(
          'Tivemos dificuldades para buscar o funil, por favor, tente novamente!',
          'Atenção'
        );
      })
      .finally(() => setLoading(false));
  }

  function HandleExport(formatFile) {
    const tiporelatorio = anchorEl.name;
    const infosReport =
      tiporelatorio === 'Eficiência'
        ? { ...filters, tiporelatorio, status: 'Aguardando cliente' }
        : { ...filters, tiporelatorio };

    const infosExport = {
      infosReport: infosReport,
      tipoReport: formatFile
    };

    configAxios
      .post('/reports/repFunnel', infosExport, { responseType: 'blob' })
      .then((response) => {
        if (response.status === 202) {
          NotificationManager.warning(
            'Não encontramos informações para os parâmetros selecionados!',
            'Atenção'
          );
          return;
        }
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        if (tiporelatorio === 'Eficiência') {
          if (formatFile === 'pdf') {
            link.setAttribute('download', 'Eficiencia por corretor.pdf');
          } else {
            link.setAttribute('download', 'Eficiencia por corretor.xlsx');
          }
        } else {
          if (formatFile === 'pdf') {
            link.setAttribute('download', 'Tempo de permanencia.pdf');
          } else {
            link.setAttribute('download', 'Tempo de permanencia.xlsx');
          }
        }
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => {
        NotificationManager.error(
          'Tivemos dificuldades para gerar o relatório! Por favor, tente novamente!',
          'Atenção'
        );
      });
  }

  return (
    <RenderPage title="Funil de vendas">
      <>
        <Stack direction="row" alignItems="center" justifyContent="start" mb={2}>
          <Grid container>
            <BreadcrumbsNav {...props} />
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="h4" gutterBottom>
                Funil de vendas
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Utilize as opções abaixo para visualizar quantidade, percentual e valor por status
              </Typography>
            </Grid>
          </Grid>
          <Stack direction="row" alignItems="center" justifyContent="end" mb={2}>
            <Button
              variant="outlined"
              onClick={() => getFunnelInfos(filters)}
              disabled={loading}
              startIcon={<Iconify icon="eva:refresh-outline" />}>
              Atualizar
            </Button>
          </Stack>
        </Stack>

        <Card>
          <CustomGrid>
            <PipelineFilters
              onFilter={(nextFilters) => {
                setFilters(nextFilters);
                getFunnelInfos(nextFilters);
              }}
              loading={loading}
            />
          </CustomGrid>
        </Card>

        <Card sx={{ mt: 2 }}>
          <CustomGrid>
            <Typography variant="h6" gutterBottom>
              Funil de propostas
            </Typography>
            <CustomInfosForm>
              Quantidade de propostas em cada etapa do processo comercial
            </CustomInfosForm>

            {!funnelOrdered.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Nenhum dado para o filtro informado.
              </Typography>
            ) : (
              <Stack alignItems="center" sx={{ mt: 3, mb: 1 }}>
                {funnelOrdered.map((item, index) => {
                  const steps = Math.max(funnelOrdered.length - 1, 1);
                  const widthPct = Math.max(48, Math.round(100 - (index * 52) / steps));
                  return (
                    <Box
                      key={'funnel-bar-' + item.statusvenda}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}>
                      {index > 0 && (
                        <Iconify
                          icon="eva:arrow-ios-downward-fill"
                          width={22}
                          height={22}
                          sx={{ color: 'text.disabled', my: 0.75 }}
                        />
                      )}
                      <Box
                        sx={{
                          width: { xs: '100%', md: widthPct + '%' },
                          bgcolor: getStatusMeta(item.statusvenda).color + '.main',
                          borderRadius: 1,
                          px: 2,
                          py: 1.5
                        }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={2}>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="subtitle2" noWrap sx={{ color: 'common.white' }}>
                              {index + 1}. {item.statusvenda}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              noWrap
                              sx={{ color: 'common.white', opacity: 0.85 }}>
                              {item.representatividade_percentual}% do total ·{' '}
                              {formatCurrencyBRL(item.valor_total)} em valor estimado
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <Typography
                              variant="h6"
                              sx={{ color: 'common.white', lineHeight: 1.15 }}>
                              {item.quantidade}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ color: 'common.white', opacity: 0.85 }}>
                              {item.quantidade === 1 ? 'proposta' : 'propostas'}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </CustomGrid>
        </Card>

        <Card sx={{ mt: 2 }}>
          <CustomGrid>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{ mb: 1 }}>
              <Typography variant="h6">Tempo de permanência por status</Typography>
              <div>
                <Button
                  sx={{ mr: 1 }}
                  variant="contained"
                  color="success"
                  id="fade-button"
                  aria-controls={open ? 'fade-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  startIcon={<Iconify icon="eva:file-text-outline" />}
                  endIcon={<KeyboardArrowDownIcon />}
                  name="Indicadores"
                  onClick={handleClick}>
                  Exportar
                </Button>
              </div>
            </Stack>
            <Box sx={{ overflowX: 'auto', mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Qtd</TableCell>
                    <TableCell align="right">
                      <TimeColumnHeader
                        label="Média"
                        color="warning"
                        icon="eva:radio-button-on-fill"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TimeColumnHeader
                        label="Mínimo"
                        color="success"
                        icon="eva:arrow-downward-fill"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TimeColumnHeader label="Máximo" color="error" icon="eva:arrow-upward-fill" />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {indicators.map((item) => (
                    <TableRow key={item.status}>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="filled"
                          color={getStatusMeta(item.status).color}
                          label={item.status}
                        />
                      </TableCell>
                      <TableCell align="right">{item.quantidade_registros}</TableCell>
                      <TableCell align="right">
                        {formatDurationFromSeconds(item.tempo_medio_segundos)}
                      </TableCell>
                      <TableCell align="right">
                        {formatDurationFromSeconds(item.tempo_minimo_segundos)}
                      </TableCell>
                      <TableCell align="right">
                        {formatDurationFromSeconds(item.tempo_maximo_segundos)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!indicators.length && (
                    <TableRow>
                      <TableCell colSpan={5}>Sem histórico de tempo no período.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </CustomGrid>
        </Card>

        <Card sx={{ mt: 2 }}>
          <CustomGrid>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{ mb: 1 }}>
              <Typography variant="h6">Eficiência por corretor</Typography>
              <div>
                <Button
                  sx={{ mr: 1 }}
                  variant="contained"
                  color="success"
                  id="fade-button"
                  aria-controls={open ? 'fade-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  startIcon={<Iconify icon="eva:file-text-outline" />}
                  endIcon={<KeyboardArrowDownIcon />}
                  name="Eficiência"
                  onClick={handleClick}>
                  Exportar
                </Button>
              </div>
            </Stack>
            <CustomInfosForm>
              Tempo em &quot;Aguardando cliente&quot; por vendedor/corretora
            </CustomInfosForm>
            <Box sx={{ overflowX: 'auto', mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Vendedor</TableCell>
                    <TableCell>Corretora</TableCell>
                    <TableCell align="right">Qtd</TableCell>
                    <TableCell align="right">
                      <TimeColumnHeader
                        label="Média"
                        color="warning"
                        icon="eva:radio-button-on-fill"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TimeColumnHeader
                        label="Mín"
                        color="success"
                        icon="eva:arrow-downward-fill"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TimeColumnHeader label="Máx" color="error" icon="eva:arrow-upward-fill" />
                    </TableCell>
                    {/* <TableCell>% da maior média</TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {efficiency.map((item, index) => {
                    // const pct = Math.round(
                    //   (Number(item.tempo_medio_segundos || 0) / maxEfficiencyAvg) * 100
                    // );
                    return (
                      <TableRow key={item.nome + '-' + item.nomefantasia + '-' + index}>
                        <TableCell>{item.nome || '-'}</TableCell>
                        <TableCell>{item.nomefantasia || '-'}</TableCell>
                        <TableCell align="right">{item.quantidade_registros}</TableCell>
                        <TableCell align="right">
                          {formatDurationFromSeconds(item.tempo_medio_segundos)}
                        </TableCell>
                        <TableCell align="right">
                          {formatDurationFromSeconds(item.tempo_minimo_segundos)}
                        </TableCell>
                        <TableCell align="right">
                          {formatDurationFromSeconds(item.tempo_maximo_segundos)}
                        </TableCell>
                        {/* <TableCell sx={{ minWidth: 160 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <LinearProgress
                              variant="determinate"
                              color={getStatusMeta('Aguardando cliente').color}
                              value={Math.min(100, pct)}
                              sx={{ flex: 1, height: 6, borderRadius: 1 }}
                            />
                            <Typography variant="caption">{pct}%</Typography>
                          </Stack>
                        </TableCell> */}
                      </TableRow>
                    );
                  })}
                  {!efficiency.length && (
                    <TableRow>
                      <TableCell colSpan={7}>Sem dados de eficiência.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </CustomGrid>
        </Card>
        <Menu
          id="fade-menu"
          MenuListProps={{
            'aria-labelledby': 'fade-button'
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          TransitionComponent={Fade}>
          <MenuItem onClick={() => HandleExport('pdf')}>
            <PictureAsPdfIcon sx={{ mr: 1 }} />
            <CustomInfosForm sx={{ fontSize: '13px', mt: 1 }}>Exportar em pdf</CustomInfosForm>
          </MenuItem>
          <MenuItem onClick={() => HandleExport('xlsx')}>
            <DocumentScannerIcon sx={{ mr: 1 }} />
            <CustomInfosForm sx={{ fontSize: '13px', mt: 1 }}>Exportar em xlsx</CustomInfosForm>
          </MenuItem>
        </Menu>
      </>
    </RenderPage>
  );
};

export default PipelineFunnel;
